import { cn } from "../../lib/utils";
import { formatDateTimeInTz, formatRelativeTime } from "../../lib/format";
import { useTimeZone } from "../../lib/timeZone";

/**
 * Timestamp — relative-by-default time ("Updated 2 hours ago") with the
 * exact date-time on hover/focus via the native `title`. Shared across all
 * portals so timestamps read uniformly.
 *
 * The zone resolves from `TimeZoneProvider` when the surrounding tree mounts
 * one, so ordinary callers pass nothing. `tz` remains available for the few
 * that render a timestamp on someone else's behalf, and an explicit value
 * always wins over the ambient one.
 *
 * WHERE THIS STILL READS PHOENIX, AND WHY IT IS NOT A BUG HERE. The portal
 * tree mounts no provider (there is no `PortalUser.TimeZone` to mount it
 * with), so its ~45 call sites fall to `FALLBACK_TIMEZONE`. That is the same
 * output they had before the provider existed — the point of routing through
 * a context that can return `null` is that the gap is now visible and
 * testable rather than hidden behind a hook that silently returns Phoenix.
 * See `../../lib/timeZone`.
 *
 * The exposure is wider than the visible text suggests: `title` is set on
 * EVERY render, so an unsupplied zone is wrong in the hover tooltip even
 * where the relative bucket ("2 hours ago") is zone-invariant.
 */
export interface TimestampProps {
  value: string | Date | null | undefined;
  tz?: string | null;
  /** Optional leading word, e.g. "Updated" → "Updated 2 hours ago". */
  prefix?: string;
  className?: string;
  /** Render absolute date-time inline instead of relative (tooltip still exact). */
  absolute?: boolean;
}

export function Timestamp({ value, tz, prefix, className, absolute }: TimestampProps) {
  // `??`, not a `=== undefined` test: a caller threading a possibly-null zone
  // through (`tz={user?.timeZone}`) means "I have nothing", which is what the
  // ambient value is for. Only a real string overrides.
  const ambient = useTimeZone();
  const zone = tz ?? ambient;
  if (value === null || value === undefined || value === "") return null;
  const exact = formatDateTimeInTz(value, zone);
  const display = absolute ? exact : formatRelativeTime(value, zone);
  if (!display) return null;
  return (
    <time
      className={cn("text-caption text-gray-mid", className)}
      title={exact}
      dateTime={typeof value === "string" ? value : value.toISOString()}
    >
      {prefix ? `${prefix} ${display}` : display}
    </time>
  );
}
